'use client';

import { omit } from 'ramda';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { Category } from '@/api/games';
import { ImageComponent } from '@/components/Image';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { oneRowSliderLiveCasinoParams } from '@/helpers/games';
import { getGamesListKey } from '@/helpers/gamesCurrencies';
import { getSliderParams } from '@/helpers/getSliderParams';
import { useResponsive } from '@/modules/client';
import { useGetCommonStore } from '@/store';
import { selectIsAwaitingWallets } from '@/store/commonStore/selectors/selectIsAwaitingWallets';
import { GamesSlider } from '../GamesSlider';

export const GamesSliderExclusive: FC = () => {
  const { t } = useTranslation('common');
  const useCommonStore = useGetCommonStore();
  const { currentMediaType } = useResponsive();

  const { listKey, exclusiveGames, fetchGames, shouldWaitForUser } =
    useCommonStore(
      useShallow((state) => {
        const key = getGamesListKey(state, Category.EXCLUSIVE);

        return {
          listKey: key,
          exclusiveGames: state.gamesByCategory?.[key],
          fetchGames: state.fetchGames,
          shouldWaitForUser: selectIsAwaitingWallets(state),
        };
      })
    );

  useEffect(() => {
    if (exclusiveGames !== null) return;

    useCommonStore.setState((state) => ({
      gamesByCategory: omit([listKey], state.gamesByCategory ?? {}),
    }));
  }, []);

  useEffect(() => {
    if (exclusiveGames !== undefined || shouldWaitForUser) return;

    let isCancelled = false;

    fetchGames({
      filter: {
        categories: {
          identifiers: [Category.EXCLUSIVE],
        },
      },
      pageSize: 21,
      page: 1,
    }).then((response) => {
      if (isCancelled) return;

      // null marks a failed request as settled, so it is not refetched on
      // every store update.
      useCommonStore.setState((state) => ({
        gamesByCategory: {
          ...state.gamesByCategory,
          [listKey]: response ?? null,
        },
      }));
    });

    return () => {
      isCancelled = true;
    };
  }, [listKey, exclusiveGames, fetchGames, shouldWaitForUser, useCommonStore]);

  if (exclusiveGames === undefined) {
    const { cols, rows } = getSliderParams(
      oneRowSliderLiveCasinoParams,
      currentMediaType
    );

    return <GamesSkeleton cols={cols} rows={rows} withTitle />;
  }

  return (
    <GamesSlider
      categoryId={Category.EXCLUSIVE}
      config={oneRowSliderLiveCasinoParams}
      dataTestId="exclusive-games-slider"
      icon={
        <ImageComponent
          alt="icon"
          height={24}
          src="/exclusiveIcon.webp"
          width={24}
        />
      }
      title={t(`common:gameCategories:${Category.EXCLUSIVE}`)}
    />
  );
};
